import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router'
import { useCookies } from 'react-cookie'
import Image from 'common/image'
import CloseButtonIcon from 'assets/drawer-close-button.svg'

interface Props {
    $isOpen: boolean
}

const DrawerContainer = styled.div<Props>`
    position: fixed;
    z-index: 99;
    width: 90vw;
    height: 100vh;
    left: 0;
    top: 0;
    bottom: 0;
    transform: ${(p) => (p.$isOpen ? 'translate(0)a' : 'translate(-100%)')};
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    background-color: #efeade;
`

const NavBarContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    background-color: #efeade;
`

interface ItemProps {
    $isSelected: boolean
}

const PageTabItem = styled(NavLink)<ItemProps>`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    padding: 20px 32px;
    cursor: pointer;
    background-color: ${(p) => (p.$isSelected ? '#F6F4F1' : '#EFEADE')};
`

const TabItemText = styled.div<ItemProps>`
    color: ${(p) => (p.$isSelected ? '#C5924D' : '#877')};
    font-size: 20px;
    font-weight: 600;
    line-height: 40px; /* 200% */
`

const CloseButton = styled.div`
    padding: 0px 32px;
    width: 100%;
    height: 93px;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    cursor: pointer;
`

const LanguageToggle = styled.div`
    margin-top: 64px;
    display: flex;
    width: 310px;
    height: 50px;
    padding: 8px 48px;
    justify-content: center;
    align-items: center;
    border-radius: 200px;
    border: 1px solid #c5924d;
    cursor: pointer;
`

const ToggleText = styled.div`
    color: #c5924d;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px; /* 125% */
    text-transform: uppercase;
`

interface CookieValues {
    name?: string
}

interface DrawerProps {
    isOpen?: boolean
    setDrawerClose: () => void
}

const DrawerMenu: React.FC<DrawerProps> = ({
    isOpen = false,
    setDrawerClose,
}) => {
    const { i18n, t } = useTranslation('', { keyPrefix: 'pagenavigator' })
    const [, setCookie] = useCookies<'weblang', CookieValues>(['weblang'])

    const langButtonClick = () => {
        const locale = i18n.language
        const now = new Date()
        // Get the current day of the month
        const currentDay = now.getDate()
        // Add 7 days (one week) to the current day
        now.setDate(currentDay + 7)

        if (locale === 'en') {
            i18n.changeLanguage('zh')
            setCookie('weblang', 'zh', { expires: now })
        } else {
            i18n.changeLanguage('en')
            setCookie('weblang', 'en', { expires: now })
        }
    }

    return (
        <DrawerContainer $isOpen={isOpen}>
            <CloseButton onClick={() => setDrawerClose()}>
                <Image
                    src={CloseButtonIcon}
                    width={16}
                    height={16}
                    alt="Drawer Close Button"
                />
            </CloseButton>
            <NavBarContainer>
                <PageTabItem
                    $isSelected={window.location.pathname === '/'}
                    to="/"
                    onClick={() => setDrawerClose()}
                >
                    <TabItemText $isSelected={window.location.pathname === '/'}>
                        {t('home')}
                    </TabItemText>
                </PageTabItem>
                <PageTabItem
                    $isSelected={
                        window.location.pathname === '/hkjcpriorityprogramme'
                    }
                    to="/hkjcpriorityprogramme"
                    onClick={() => setDrawerClose()}
                >
                    <TabItemText
                        $isSelected={
                            window.location.pathname ===
                            '/hkjcpriorityprogramme'
                        }
                    >
                        {t('hkjcpriorityprogramme')}
                    </TabItemText>
                </PageTabItem>
                <PageTabItem
                    $isSelected={window.location.pathname === '/earnwins'}
                    to="/earnwins"
                    onClick={() => setDrawerClose()}
                >
                    <TabItemText
                        $isSelected={window.location.pathname === '/earnwins'}
                    >
                        {t('earnwins')}
                    </TabItemText>
                </PageTabItem>
                <PageTabItem
                    $isSelected={window.location.pathname === '/spendwins'}
                    to="/spendwins"
                    onClick={() => setDrawerClose()}
                >
                    <TabItemText
                        $isSelected={window.location.pathname === '/spendwins'}
                    >
                        {t('spendwins')}
                    </TabItemText>
                </PageTabItem>
                <PageTabItem
                    $isSelected={window.location.pathname === '/helpcentre'}
                    to="/helpcentre"
                    onClick={() => setDrawerClose()}
                >
                    <TabItemText
                        $isSelected={window.location.pathname === '/helpcentre'}
                    >
                        {t('helpcentre')}
                    </TabItemText>
                </PageTabItem>
                <LanguageToggle onClick={() => langButtonClick()}>
                    <ToggleText>{t('langButtonText')}</ToggleText>
                </LanguageToggle>
            </NavBarContainer>
        </DrawerContainer>
    )
}

export default DrawerMenu
